import { API_URL } from '../utils/config';

export const API_BASE_URL = API_URL;

export const apiRequest = async (endpoint, method = 'GET', body = null) => {
  try {
    const token = localStorage.getItem('accessToken');

    const headers = { Accept: 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const options = { method, headers, credentials: 'include' };

    if (body) {
      if (body instanceof FormData) {
        options.body = body; // don't set Content-Type
      } else {
        headers['Content-Type'] = 'application/json';
        options.body = JSON.stringify(body);
      }
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, options);

    // Read text once so we can handle JSON / empty / non-JSON safely
    const contentType = response.headers.get('content-type') || '';
    const text = response.status === 204 ? '' : await response.text();

    if (!response.ok) {
      // For 422 and 400 errors, try to parse and return structured error data
      if (response.status === 422 || response.status === 400) {
        try {
          let errorData;
          // Try to parse as JSON
          if (contentType.includes('application/json')) {
            errorData = JSON.parse(text);
          } else {
            // Try parsing text as JSON even if content-type doesn't say so
            try {
              errorData = JSON.parse(text);
            } catch (e) {
              // Not JSON, continue with text
            }
          }
          
          if (errorData) {
            const error = new Error(errorData?.detail?.message || `Validation failed`);
            error.status = response.status;
            error.data = errorData;
            error.text = text;
            throw error;
          }
        } catch (parseError) {
          // If parsing fails, continue to generic error handling below
        }
      }
      
      // Generic error handling for all other status codes
      const error = new Error(`API request failed with status ${response.status}: ${text}`);
      error.status = response.status;
      error.text = text;
      
      // Try to parse error text as JSON if it looks like JSON (for cases where content-type is wrong)
      try {
        const parsedText = JSON.parse(text);
        if (parsedText && typeof parsedText === 'object') {
          error.data = parsedText;
        }
      } catch (e) {
        // Not JSON, keep as text
      }
      
      throw error;
    }

    // No content? return null
    if (!text) return null;

    // JSON response
    if (contentType.includes('application/json')) {
      return JSON.parse(text);
    }

    // Fallback: return raw text for non-JSON endpoints
    return text;
  } catch (error) {
    console.error(`Error making ${method} request to ${endpoint}:`, error);
    throw error;
  }
};


export const exportOrders = async (params = {}) => {
  const token = localStorage.getItem('accessToken');
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([,v]) => v !== undefined && v !== null && v !== ''))
  ).toString();
  const url = `${API_BASE_URL}/orders/export${qs ? `?${qs}` : ''}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      Accept: '*/*',
    },
    credentials: 'include',
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Export failed (${res.status}): ${text}`);
  }
  return await res.blob(); // xlsx or csv (fallback)
};

export const getUserById = async (userId) =>
    await apiRequest(`/dashboard/admin/users/${userId}`, 'GET');

export const updateUser = async (userId, data) =>
    await apiRequest(`/dashboard/admin/users/${userId}`, 'PUT', data);

export const deleteUser = async (userId) =>
    await apiRequest(`/dashboard/admin/users/${userId}`, 'DELETE');

export const deactivateUser = async (userId) =>
    await apiRequest(`/dashboard/admin/users/${userId}/deactivate`, 'POST');

export const activateUser = async (userId) =>
    await apiRequest(`/dashboard/admin/users/${userId}/activate`, 'POST');

export const createTeamMember = async (data) =>
    await apiRequest('/dashboard/admin/users', 'POST', data);

export const updateUserRoles = async (userId, roles) =>
    await apiRequest(`/dashboard/admin/users/${userId}/roles`, 'PUT', { roles });

export const resetUserPassword = async (userId, data) =>
    await apiRequest(`/dashboard/admin/users/${userId}/reset-password`, 'POST', data);

export const getUserProfile = async (userId) =>
    await apiRequest(`/dashboard/admin/users/${userId}/profile`, 'GET');

export const getUsers = async (query) =>
    await apiRequest(`/dashboard/admin/users?${query}`, 'GET');

// ─── Customer Management ───────────────────────────────────────
export const getCustomers = async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return await apiRequest(`/dashboard/admin/customers${query ? '?' + query : ''}`, 'GET');
};

export const getCustomerProfile = async (userId) =>
    await apiRequest(`/dashboard/admin/customers/${userId}/profile`, 'GET');

export const updateCustomerProfile = async (userId, data) =>
    await apiRequest(`/dashboard/admin/customers/${userId}/profile`, 'PUT', data);

export const freezeCustomer = async (userId) =>
    await apiRequest(`/dashboard/admin/customers/${userId}/freeze`, 'POST');

export const unfreezeCustomer = async (userId) =>
    await apiRequest(`/dashboard/admin/customers/${userId}/unfreeze`, 'POST');

export const getCustomerOrders = async (userId) =>
    await apiRequest(`/dashboard/admin/customers/${userId}/orders`, 'GET');

export const exportCustomerOrders = async (userId) => {
    const token = localStorage.getItem('accessToken');
    const resp = await fetch(`${API_BASE_URL}/dashboard/admin/customers/${userId}/orders/export`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), },
        credentials: 'include',
    });
    if (!resp.ok) throw new Error('Export failed');
    return resp.blob();
};

export const getAdminDashboard = async () =>
    await apiRequest('/dashboard/admin', 'GET');

export const getAdminStats = async () =>
    await apiRequest('/dashboard/admin/stats', 'GET');

export const updateAddress = async (addressId, data) =>
    await apiRequest(`/dashboard/profile/addresses/${addressId}`, 'PUT', data);

export const deleteAddress = async (addressId) =>
    await apiRequest(`/dashboard/profile/addresses/${addressId}`, 'DELETE');

export const getAddresses = async () =>
    await apiRequest('/dashboard/profile/addresses', 'GET');

export const createAddress = async (data) =>
    await apiRequest('/dashboard/profile/addresses', 'POST', data);

export const getProfile = async () =>
    await apiRequest('/dashboard/profile', 'GET');

export const getProfileByOrderId = async (orderId) =>
    await apiRequest(`/dashboard/profile/${orderId}`, 'GET');

export const updateProfile = async (data) =>
    await apiRequest('/dashboard/profile', 'PUT', data);

export const getBusinessSubUsers = async () =>
    await apiRequest('/dashboard/business-subusers', 'GET');

export const createBusinessSubUser = async (data) =>
    await apiRequest('/dashboard/business-subusers', 'POST', data);

export const updateBusinessSubUser = async (subUserId, data) =>
    await apiRequest(`/dashboard/business-subusers/${subUserId}`, 'PUT', data);

export const deactivateBusinessSubUser = async (subUserId) =>
    await apiRequest(`/dashboard/business-subusers/${subUserId}`, 'DELETE');

export const getCustomerSubUsers = async (userId) =>
    await apiRequest(`/dashboard/admin/customers/${userId}/sub-users`, 'GET');

export const adminCreateCustomerSubUser = async (parentUserId, data) =>
    await apiRequest(`/dashboard/admin/customers/${parentUserId}/sub-users`, 'POST', data);

export const adminUpdateCustomerSubUser = async (parentUserId, subUserId, data) =>
    await apiRequest(`/dashboard/admin/customers/${parentUserId}/sub-users/${subUserId}`, 'PUT', data);

export const adminDeactivateCustomerSubUser = async (parentUserId, subUserId) =>
    await apiRequest(`/dashboard/admin/customers/${parentUserId}/sub-users/${subUserId}`, 'DELETE');

// ===== Companies (business accounts) =====
export const getMyCompany = async () =>
    await apiRequest('/companies/me', 'GET');

export const adminListCompanies = async (query = '') =>
    await apiRequest(`/dashboard/admin/companies${query ? `?q=${encodeURIComponent(query)}` : ''}`, 'GET');

export const adminGetCompany = async (companyId) =>
    await apiRequest(`/dashboard/admin/companies/${companyId}`, 'GET');

export const adminUpdateCompany = async (companyId, data) =>
    await apiRequest(`/dashboard/admin/companies/${companyId}`, 'PUT', data);

export const adminTransferCompanyPrimary = async (companyId, data) =>
    await apiRequest(`/dashboard/admin/companies/${companyId}/transfer-primary`, 'POST', data);

export const adminGetCompanyOwnershipHistory = async (companyId) =>
    await apiRequest(`/dashboard/admin/companies/${companyId}/ownership-history`, 'GET');

// Self-serve: current primary hands over ownership to an active company
// member. After success the client should force a re-login.
export const businessTransferPrimary = async (data) =>
    await apiRequest('/dashboard/business-subusers/transfer-primary', 'POST', data);

export const getUserHtml = async () =>
    await apiRequest('/dashboard/user.html', 'GET');

export const getAdminHtml = async () =>
    await apiRequest('/dashboard/admin.html', 'GET');

export const getDashboardUser = async () =>
    await apiRequest('/dashboard/user', 'GET');

// Category functions moved to bottom of file

export const getProductById = async (productId) =>
    await apiRequest(`/products/${productId}`, 'GET');

export const updateProduct = async (productId, data) =>
    await apiRequest(`/products/${productId}`, 'PUT', data);

export const deleteProduct = async (productId) =>
    await apiRequest(`/products/${productId}`, 'DELETE');

export const updateProductStock = async (productId, quantity) =>
    await apiRequest(`/products/${productId}/update-stock?quantity=${quantity}`, 'POST');

export const bulkUploadProducts = async (formData) =>
    await apiRequest('/products/manage/bulk-upload', 'POST', formData);

export const bulkUploadPreview = async (formData) =>
    await apiRequest('/products/manage/bulk-upload-preview', 'POST', formData);

export const exportProducts = async (query) =>
    await apiRequest(`/products/manage/export?${query}`, 'POST');

export const exportProductsCsv = async (filterQueryString) => {
    const token = localStorage.getItem('accessToken');
    const url = `${API_BASE_URL}/products/export/csv${filterQueryString ? `?${filterQueryString}` : ''}`;
    const res = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Export failed');
    return await res.blob();
};

export const getUploadHistory = async (query) =>
    await apiRequest(`/products/manage/upload-history?${query}`, 'GET');

export const createProduct = async (data) =>
    await apiRequest('/products/', 'POST', data);

export const getProducts = async (query) => {
    const queryString = query ? `?${query}` : '';
    return await apiRequest(`/products/${queryString}`, 'GET');
};

export const getUploadLogById = async (logId) =>
    await apiRequest(`/products/upload-history/${logId}`, 'GET');

export const bulkStockUpdate = async (data) =>
    await apiRequest('/products/bulk-stock-update', 'POST', data);

export const getLowStockProducts = async (query) =>
    await apiRequest(`/products/low-stock?${query}`, 'GET');

export async function getProductStats(queryString = '') {
  const qs = queryString ? `?${queryString}` : '';
  return apiRequest(`/products/stats${qs}`, 'GET'); 
}

export const getManufacturers = async () =>
  await apiRequest('/products/brands/list', 'GET');


export const getOrderById = async (orderId) =>
    await apiRequest(`/orders/${orderId}`, 'GET');

export const deleteOrder = async (orderId) =>
    await apiRequest(`/orders/${orderId}`, 'DELETE');

export const updateOrderStatus = async (orderId, data) =>
    await apiRequest(`/orders/${orderId}/status`, 'PUT', data);

export const createOrder = async (data) =>
    await apiRequest('/orders/', 'POST', data);

export const createOrderFromCart = async (data = {}) =>
  await apiRequest('/orders/from-cart', 'POST', data);

export const getOrders = async (query) =>
    await apiRequest(`/orders/?${query}`, 'GET');

export const getOrderStats = async () =>
    await apiRequest('/orders/stats', 'GET');

export const confirmOrderAndSendEmails = async (orderId) =>
  await apiRequest(`/orders/${orderId}/confirm`, 'POST');

export const updateOrderShipping = async (orderId, shippingCost) =>
  await apiRequest(`/orders/${orderId}/shipping`, 'PUT', { shipping_cost: shippingCost });

export const updateOrderTax = async (orderId, taxAmount) =>
  await apiRequest(`/orders/${orderId}/tax`, 'PUT', { tax_amount: taxAmount });

export const updateOrderShippingBaseCost = async (orderId, shippingBaseCost) =>
  await apiRequest(`/orders/${orderId}/shipping-base-cost`, 'PUT', { shipping_base_cost: shippingBaseCost });

export const updateOrderShippingAdditionalCost = async (orderId, shippingAdditionalCost) =>
  await apiRequest(`/orders/${orderId}/shipping-additional-cost`, 'PUT', { shipping_additional_cost: shippingAdditionalCost });

export const updateOrderItemAvailability = async (orderId, itemId, availability) =>
  await apiRequest(`/orders/${orderId}/items/${itemId}/availability`, 'PUT', { availability });

export const updateOrderItem = async (orderId, itemId, data) =>
    await apiRequest(`/orders/${orderId}/items/${itemId}`, 'PUT', data);

export const confirmPrice = async (orderId, itemId, unitPrice) =>
    await apiRequest(`/orders/${orderId}/items/${itemId}/confirm-price`, 'PUT', { unit_price: unitPrice });

export const calculateOrderShipping = async (orderId) =>
    await apiRequest(`/orders/${orderId}/shipping-calculation`, 'GET');

export const getOrderStatusHistory = async (orderId) =>
    await apiRequest(`/orders/${orderId}/status-history`, 'GET');

export const cancelOrder = async (orderId, cancellationReason) =>
    await apiRequest(`/orders/${orderId}/cancel`, 'POST', { cancellation_reason: cancellationReason });

export const applyOrderCredit = async (orderId, creditAmount, billableAmount = null) => {
    const body = { credit_amount: creditAmount };
    if (billableAmount !== null && billableAmount !== undefined && Number.isFinite(Number(billableAmount))) {
        body.billable_amount = Number(billableAmount);
    }
    return await apiRequest(`/orders/${orderId}/apply-credit`, 'POST', body);
};

export const sendPoToVendor = async (orderId, vendor = null) => {
    const body = vendor ? { vendor } : {};
    return await apiRequest(`/orders/${orderId}/send-po-to-vendor`, 'POST', body);
};

export const resendPreOrderToVendor = async (orderId, vendor = null) => {
    const body = vendor ? { vendor } : {};
    return await apiRequest(`/orders/${orderId}/resend-pre-order-to-vendor`, 'POST', body);
};

// User order modification APIs
export const updateUserOrderItemQuantity = async (orderId, itemId, quantity) =>
    await apiRequest(`/orders/${orderId}/items/${itemId}/quantity`, 'PUT', { quantity });

export const removeUserOrderItem = async (orderId, itemId) =>
    await apiRequest(`/orders/${orderId}/items/${itemId}`, 'DELETE');

export const requestReconfirmation = async (orderId) =>
    await apiRequest(`/orders/${orderId}/request-reconfirmation`, 'POST');

// Admin validate order API
export const validateOrder = async (orderId) =>
    await apiRequest(`/orders/${orderId}/validate`, 'POST');

export const updateOrderAmounts = async (orderId, amounts) =>
  await apiRequest(`/orders/${orderId}/amounts`, 'PUT', amounts);

export const createPaymentLink = async (data) =>
    await apiRequest('/payments/links', 'POST', data);

export const getPaymentLinkByOrderId = async (orderId) =>
    await apiRequest(`/payments/links/${orderId}`, 'GET');

export const updateCartItem = async (itemId, data) =>
    await apiRequest(`/cart/items/${itemId}`, 'PUT', data);

export const deleteCartItem = async (itemId) =>
    await apiRequest(`/cart/items/${itemId}`, 'DELETE');

export const addCartItem = async (data) =>
    await apiRequest('/cart/items', 'POST', data);

export const getCart = async () =>
    await apiRequest('/cart', 'GET');

export const clearCart = async () =>
    await apiRequest('/cart/clear', 'DELETE');

export const getQuoteSummary = async () =>
    await apiRequest('/quotes/summary', 'GET');

export const submitQuote = async () =>
    await apiRequest('/quotes/submit', 'POST');

export const getRoot = async () =>
    await apiRequest('/', 'GET');

export const getHealth = async () =>
    await apiRequest('/health', 'GET');

export const getLoginHtml = async () =>
    await apiRequest('/login.html', 'GET');

export const getSignupHtml = async () =>
    await apiRequest('/signup.html', 'GET');

export const getTestSessionHtml = async () =>
    await apiRequest('/test-session.html', 'GET');

export const uploadFile = async (formData) =>
    await apiRequest('/uploadfile/', 'POST', formData);

export const getMyOrders = async (query) =>
    await apiRequest(`/orders/?${query}`, 'GET');

export const listUploadLogs = async (query) =>
    await apiRequest(`/products/uploads`, 'GET');

export const getUploadLog = async (logId) =>
    await apiRequest(`/products/uploads/${logId}`, 'GET');

export const getCatalogImpact = async (logId) =>
    await apiRequest(`/products/uploads/${logId}/catalog-impact`, 'GET');

// export const getStagedItems = async (logId) =>
//     await apiRequest(`/products/uploads/${logId}/items`, 'GET');

export const getStagedItems = async (logId, params = {}) => {
  const qs = new URLSearchParams(params).toString();
  const suffix = qs ? `?${qs}` : '';
  return await apiRequest(`/products/uploads/${logId}/items${suffix}`, 'GET');
}

export const updateStagedItem = async (logId, itemId, payload) =>
    await apiRequest(`/products/uploads/${logId}/items/${itemId}`, 'PUT', payload);

export const importStaged = async (logId) =>
    await apiRequest(`/products/uploads/${logId}/import`, 'POST');

export const uploadAndStage = async (data) => {
  let formData;
  if (data instanceof FormData) {
    formData = data;
  } else if (data instanceof File) {
    formData = new FormData();
    formData.append('file', data, data.name || 'upload.csv');
  } else {
    throw new Error('uploadAndStage expects a File or FormData');
  }
  return await apiRequest('/products/uploads', 'POST', formData);
};


export const listProducts = async (  page = 1, pageSize = 12, search, categoryId, inStock) =>
    await apiRequest('/products/uploads/', 'GET');

export const chat = async (message) =>
    await apiRequest('/chat/','POST', { message });

export const ragChat = async (message, sessionId = 'default') =>
    await apiRequest('/chat/rag/', 'POST', { message, session_id: sessionId });

export const filterProductsByChat = async (query, sessionId, filters) =>
    await apiRequest('/filter-by-chat', 'POST', { 
        query, 
        session_id: sessionId, 
        filters 
    });

// AI Inference API
export const runAIInference = async (logId, requestData) =>
    await apiRequest(`/products/uploads/${logId}/ai-inference`, 'POST', requestData);

// Async AI Processing API
export const startAIInference = async (logId, requestData) =>
    await apiRequest(`/ai-processing/uploads/${logId}/start`, 'POST', requestData);

export const getAIJobStatus = async (jobId) =>
    await apiRequest(`/ai-processing/jobs/${jobId}/status`, 'GET');

export const cancelAIJob = async (jobId) =>
    await apiRequest(`/ai-processing/jobs/${jobId}/cancel`, 'GET');

// Async Image Processing API
export const startImageExtraction = async (logId, productIds = null) => {
    const url = `/image-processing/uploads/${logId}/start`;
    const body = {
        product_ids: productIds && productIds.length > 0 ? productIds : null
    };
    return await apiRequest(url, 'POST', body);
};

export const getImageJobStatus = async (jobId) =>
    await apiRequest(`/image-processing/jobs/${jobId}/status`, 'GET');

export const cancelImageJob = async (jobId) =>
    await apiRequest(`/image-processing/jobs/${jobId}/cancel`, 'POST');

export async function getMcbOrderStatus(orderId) {
  return apiRequest(`/payments/mcb/status/${orderId}`, 'GET');
}

export async function createMcbSession({ order_id, amount, currency, description }) {
  return apiRequest('/payments/mcb/session', 'POST', {
    order_id,
    amount,
    currency,
    description,
  });
}

// Image search and upload functions
export async function searchProductImages(stagedProductId) {
  return apiRequest(`/staged-products/${stagedProductId}/search-images`, 'POST');
}

export async function uploadCustomImage(productId, file, isStagedProduct = true) {
  const formData = new FormData();
  formData.append('file', file);
  
  // Use staged product endpoint by default (for ReviewStagedPanel)
  // If isStagedProduct is false, use regular product endpoint
  const endpoint = isStagedProduct 
    ? `/staged-products/${productId}/upload-image`
    : `/products/${productId}/upload-custom-image`;
  
  return apiRequest(endpoint, 'POST', formData, {
    'Content-Type': 'multipart/form-data',
  });
}

export async function selectProductImages(stagedProductId, selectedImages) {
  return apiRequest(`/staged-products/${stagedProductId}/select-images`, 'PUT', selectedImages);
}

export async function bulkExtractImages(uploadLogId) {
  return apiRequest(`/staged-products/bulk-extract-images?upload_log_id=${uploadLogId}`, 'POST');
}

// Individual product image extraction API
export async function extractImagesForProduct(stagedProductId) {
  return apiRequest(`/staged-products/${stagedProductId}/extract-images`, 'POST');
}

// Individual regular product image extraction API
export async function extractImagesForRegularProduct(productId) {
  return apiRequest(`/products/${productId}/extract-images`, 'POST');
}

// Extract images for new product based on details (before product creation)
export async function extractImagesByProductDetails(productDetails) {
  return apiRequest('/products/extract-images-by-details', 'POST', productDetails);
}

// AI inference for new product based on details (before product creation)
export async function runAIInferenceByProductDetails(productDetails) {
  return apiRequest('/products/ai-inference-by-details', 'POST', productDetails);
}

// Select images for regular product API
export async function selectRegularProductImages(productId, imageUrls) {
  return apiRequest(`/products/${productId}/select-images`, 'PUT', imageUrls);
}

// Upload extracted image for regular product API
export async function uploadExtractedImageForRegularProduct(productId, imageUrl) {
  return apiRequest(`/products/${productId}/upload-extracted-image`, 'POST', { image_url: imageUrl });
}

export async function getExtractedImages(stagedProductId) {
  return apiRequest(`/staged-products/${stagedProductId}/extracted-images`, 'GET');
}

export async function deleteStagedProductImage(stagedProductId, imageUrl) {
  return apiRequest(`/staged-products/${stagedProductId}/images?image_url=${encodeURIComponent(imageUrl)}`, 'DELETE');
}

// Robust Processing API functions
export const startRobustAI = async (logId, requestData) =>
    await apiRequest(`/processing/uploads/${logId}/ai/start`, 'POST', requestData);

export const startRobustImageExtraction = async (logId, requestData) =>
    await apiRequest(`/processing/uploads/${logId}/images/start`, 'POST', requestData);

export const getRobustJobStatus = async (jobId) =>
    await apiRequest(`/processing/jobs/${jobId}/status`, 'GET');

export const cancelRobustJob = async (jobId) =>
    await apiRequest(`/processing/jobs/${jobId}/cancel`, 'POST');

export const cleanupJobs = async () =>
    await apiRequest('/processing/cleanup', 'POST');

// MCP Chat API functions
export const clearChatHistoryMCP = async (sessionId) => {
    try {
        const response = await apiRequest(`/chat/mcp/clear/${sessionId}`, 'DELETE');
        return response;
    } catch (error) {
        console.error("Error clearing chat history:", error);
        throw error;
    }
};

export const chatApiMCP = async (message, sessionId) => {
    try {
        const response = await apiRequest('/chat/mcp/', 'POST', {
            message: message,
            session_id: sessionId,
        });
        return response;
    } catch (error) {
        console.error("Error in chatApiMCP:", error);
        throw error;
    }
};

// Category Management API functions
export const getCategories = async () => {
    return apiRequest('/categories/', 'GET');
};

export const createCategory = async (categoryData) => {
    return apiRequest('/categories/', 'POST', categoryData);
};

export const updateCategory = async (categoryId, categoryData) => {
    return apiRequest(`/categories/${categoryId}`, 'PUT', categoryData);
};

export const deleteCategory = async (categoryId) => {
    return apiRequest(`/categories/${categoryId}`, 'DELETE');
};

// Manufacturer Management API functions
export const getManufacturerList = async () => {
    return apiRequest('/manufacturers/', 'GET');
};

export const getVendors = async () => {
    return apiRequest('/products/vendors/', 'GET');
};

export const createManufacturer = async (manufacturerData) => {
    return apiRequest('/manufacturers/', 'POST', manufacturerData);
};

export const updateManufacturer = async (manufacturerId, manufacturerData) => {
    return apiRequest(`/manufacturers/${manufacturerId}`, 'PUT', manufacturerData);
};

export const deleteManufacturer = async (manufacturerId) => {
    return apiRequest(`/manufacturers/${manufacturerId}`, 'DELETE');
};

// User Management API functions
export const getAllUsers = async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.is_admin !== undefined && params.is_admin !== null) queryParams.append('is_admin', params.is_admin);
    if (params.is_active !== undefined && params.is_active !== null) queryParams.append('is_active', params.is_active);
    if (params.skip) queryParams.append('skip', params.skip);
    if (params.limit) queryParams.append('limit', params.limit);
    
    const queryString = queryParams.toString();
    return apiRequest(`/dashboard/admin/users${queryString ? `?${queryString}` : ''}`, 'GET');
};

// Shipment APIs
export const createShipments = async (orderId) => {
    return apiRequest(`/orders/${orderId}/shipments/create`, 'POST');
};

export const getOrderShipments = async (orderId) => {
    return apiRequest(`/orders/${orderId}/shipments`, 'GET');
};

export const generateDHLShipment = async (orderId, shipmentId) => {
    return apiRequest(`/orders/${orderId}/shipments/${shipmentId}/generate-dhl`, 'POST');
};

export const updateShipmentTracking = async (orderId, shipmentId, trackingLink) => {
    return apiRequest(`/orders/${orderId}/shipments/${shipmentId}/tracking?tracking_link=${encodeURIComponent(trackingLink)}`, 'PUT');
};

export const calculateShipmentShipping = async (orderId, shipmentId) => {
    return apiRequest(`/orders/${orderId}/shipments/${shipmentId}/calculate-shipping`, 'POST');
};

export const updateShipmentFinancials = async (orderId, shipmentId, shippingCost) => {
    const params = new URLSearchParams();
    if (shippingCost !== null && shippingCost !== undefined) {
        params.append('shipping_cost', shippingCost);
    }
    return apiRequest(`/orders/${orderId}/shipments/${shipmentId}/financials?${params.toString()}`, 'PUT');
};

export const confirmShipmentShipping = async (orderId, shipmentId, shippingAmount) => {
    if (shippingAmount === undefined || shippingAmount === null || isNaN(shippingAmount)) {
        throw new Error('Shipping amount is required');
    }
    return apiRequest(`/orders/${orderId}/shipments/${shipmentId}/confirm-shipping?shipping_amount=${encodeURIComponent(shippingAmount)}`, 'POST');
};

export const updateShipmentShipping = async (orderId, shipmentId, adjustment) => {
    return apiRequest(`/orders/${orderId}/shipments/${shipmentId}/shipping?adjustment=${adjustment}`, 'PUT');
};

export const updateShipmentAdditionalCost = async (orderId, shipmentId, additionalCost) => {
    return apiRequest(`/orders/${orderId}/shipments/${shipmentId}/additional-cost?additional_cost=${additionalCost}`, 'PUT');
};

export const uploadShipmentDocuments = async (orderId, shipmentId, files) => {
    const formData = new FormData();
    files.forEach(file => {
        formData.append('files', file);
    });
    return apiRequest(`/orders/${orderId}/shipments/${shipmentId}/documents`, 'POST', formData);
};

export const listShipmentDocuments = async (orderId, shipmentId) => {
    return apiRequest(`/orders/${orderId}/shipments/${shipmentId}/documents`, 'GET');
};

export const deleteShipmentDocument = async (orderId, shipmentId, documentId) => {
    return apiRequest(`/orders/${orderId}/shipments/${shipmentId}/documents/${documentId}`, 'DELETE');
};

export const downloadShipmentDocument = async (orderId, shipmentId, documentId, fileName) => {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/orders/${orderId}/shipments/${shipmentId}/documents/${documentId}/download`, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`,
        },
    });
    
    if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
    }
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || `document_${documentId}`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
};

// Payment Request APIs (Admin)
export const createPaymentRequest = async (formData) =>
    await apiRequest('/payment-requests/', 'POST', formData);

export const getPaymentRequests = async (query) =>
    await apiRequest(`/payment-requests/?${query}`, 'GET');

export const getPaymentRequestById = async (id) =>
    await apiRequest(`/payment-requests/${id}`, 'GET');

export const cancelPaymentRequest = async (id) =>
    await apiRequest(`/payment-requests/${id}/cancel`, 'PUT');

export const resendPaymentRequestEmail = async (id) =>
    await apiRequest(`/payment-requests/${id}/resend-email`, 'POST');

export const uploadPaymentRequestDocuments = async (id, formData) =>
    await apiRequest(`/payment-requests/${id}/documents`, 'POST', formData);

export const getPaymentRequestDocuments = async (id) =>
    await apiRequest(`/payment-requests/${id}/documents`, 'GET');

export const deletePaymentRequestDocument = async (id, docId) =>
    await apiRequest(`/payment-requests/${id}/documents/${docId}`, 'DELETE');

export const downloadPaymentRequestDocument = async (id, docId, fileName) => {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/payment-requests/${id}/documents/${docId}/download`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) throw new Error(`Download failed: ${response.statusText}`);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || `document_${docId}`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
};

// Payment Request APIs (Public - no auth)
export const getPublicPaymentRequest = async (token) =>
    await apiRequest(`/payment-requests/public/${token}`, 'GET');

export const initiatePaymentRequestPayment = async (token) =>
    await apiRequest(`/payment-requests/public/${token}/pay`, 'POST');

export const getPaymentRequestPaymentStatus = async (token) =>
    await apiRequest(`/payment-requests/public/${token}/status`, 'GET');

// Role Management APIs
export const getPermissions = async () =>
    apiRequest('/dashboard/admin/permissions', 'GET');

export const getRoles = async () =>
    apiRequest('/dashboard/admin/roles', 'GET');

export const updateRolePermissions = async (roleId, permissions) =>
    apiRequest(`/dashboard/admin/roles/${roleId}/permissions`, 'PUT', { permissions });

export const createRole = async (name, description, permissions) =>
    apiRequest('/dashboard/admin/roles', 'POST', { name, description, permissions });

// Internal notes (support agent / read-only team member)
export const addInternalNote = async (orderId, note) =>
    apiRequest(`/orders/${orderId}/internal-note`, 'POST', { note });

// ─── Vendor Management ────────────────────────────────────────────
export const getVendorList = async () =>
    apiRequest('/dashboard/admin/vendors', 'GET');

export const getVendorById = async (id) =>
    apiRequest(`/dashboard/admin/vendors/${id}`, 'GET');

export const createVendor = async (data) =>
    apiRequest('/dashboard/admin/vendors', 'POST', data);

export const updateVendor = async (id, data) =>
    apiRequest(`/dashboard/admin/vendors/${id}`, 'PUT', data);

export const deactivateVendor = async (id) =>
    apiRequest(`/dashboard/admin/vendors/${id}/deactivate`, 'POST');

export const activateVendor = async (id) =>
    apiRequest(`/dashboard/admin/vendors/${id}/activate`, 'POST');

export const getActiveVendors = async () =>
    apiRequest('/vendors/active', 'GET');

export const getInternalUsers = async () =>
    apiRequest('/dashboard/admin/internal-users', 'GET');
